/**
 * Integrytas Group - Base App (MOBILE)
 * Page: MTA.RULES — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/MtaRuleManagerPage.tsx. A DISTINCT
 * presentation (dense card list + facet chips + status pills + bottom-sheet
 * detail + edit sheet) over the SAME data/logic layer only:
 *   - reads via apiDb (mta_automation_rule + a mta_action roll-up for the
 *     per-rule action list/count — a scannable, mobile-friendly summary of the
 *     desktop rule/action editor),
 *   - the activate/deactivate + scalar edits (event, description, active) write
 *     through the Common Routine (routedUpdate) exactly like the desktop page —
 *     §2 single write path; never a direct apiDb write.
 *
 * mta_automation_rule is a technical config table: internal_code + entity_name
 * are natural identifiers (a rule code and the table it fires on), and on_event
 * / action_type / channel are CHECK enums — so there is no _tl lookup to resolve,
 * but every displayed enum label goes through i18n labelled constants, never a
 * bare stored literal (§17.B11). Y/N renders as status pills (§17.B10).
 *
 * Authoring conditions JSON and the mta_action rows stays on the desktop (shown
 * read-only here, mirroring MobileUserEditSheet's read-only roles). §16: never
 * the desktop grid re-hosted.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Search, Inbox, Power, X, SquarePen, Zap } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedUpdate } from '../../lib/routed_writes';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'MTA.RULES';

type OnEvent = 'ADD' | 'CHANGE' | 'DELETE' | 'ANY';
type ActionType = 'TASK' | 'MESSAGE';
type Channel = 'INAPP' | 'EMAIL' | 'WEBHOOK';

interface MobileActionRow {
  mta_action_id: number;
  action_type: ActionType;
  channel: Channel | null;
  is_active: 'Y' | 'N';
  sort_order: number;
}

interface MobileRuleRow {
  pk: number;
  internal_code: string;
  entity_name: string;
  on_event: OnEvent;
  conditions: unknown;
  is_active: 'Y' | 'N';
  description: string | null;
  actions: MobileActionRow[];
  active_action_count: number;
}

type StatusFacet = 'all' | 'active' | 'inactive';
type EventFacet = 'all' | OnEvent;

// §17.B11: CHECK-enum labels via i18n labelled constants — never a bare literal.
const ON_EVENT_OPTIONS: { value: OnEvent; key: string; fallback: string }[] = [
  { value: 'ADD', key: 'mobile.mta.event.ADD', fallback: 'On add' },
  { value: 'CHANGE', key: 'mobile.mta.event.CHANGE', fallback: 'On change' },
  { value: 'DELETE', key: 'mobile.mta.event.DELETE', fallback: 'On delete' },
  { value: 'ANY', key: 'mobile.mta.event.ANY', fallback: 'Any change' },
];

const ACTION_TYPE_OPTIONS: { value: ActionType; key: string; fallback: string }[] = [
  { value: 'TASK', key: 'mobile.mta.action_type.TASK', fallback: 'Task' },
  { value: 'MESSAGE', key: 'mobile.mta.action_type.MESSAGE', fallback: 'Message' },
];

const CHANNEL_OPTIONS: { value: Channel; key: string; fallback: string }[] = [
  { value: 'INAPP', key: 'mobile.mta.channel.INAPP', fallback: 'In-app' },
  { value: 'EMAIL', key: 'mobile.mta.channel.EMAIL', fallback: 'Email' },
  { value: 'WEBHOOK', key: 'mobile.mta.channel.WEBHOOK', fallback: 'Webhook' },
];

function eventLabel(t: TFunction, value: OnEvent): string {
  const opt = ON_EVENT_OPTIONS.find((o) => o.value === value);
  return opt ? t(opt.key, opt.fallback) : value;
}

function actionTypeLabel(t: TFunction, value: ActionType): string {
  const opt = ACTION_TYPE_OPTIONS.find((o) => o.value === value);
  return opt ? t(opt.key, opt.fallback) : value;
}

function channelLabel(t: TFunction, value: Channel): string {
  const opt = CHANNEL_OPTIONS.find((o) => o.value === value);
  return opt ? t(opt.key, opt.fallback) : value;
}

/** Compact, human summary of the conditions JSON blob (no raw JSON on a phone). */
function conditionsSummary(t: TFunction, conditions: unknown): string {
  if (conditions && typeof conditions === 'object') {
    const c = conditions as { match?: unknown; conditions?: unknown };
    const list = Array.isArray(c.conditions) ? c.conditions : [];
    const matchRaw = typeof c.match === 'string' ? c.match.toLowerCase() : 'all';
    const matchLabel =
      matchRaw === 'any'
        ? t('mobile.mta.match_any', 'match any')
        : t('mobile.mta.match_all', 'match all');
    if (list.length > 0) {
      return `${matchLabel} · ${t('mobile.mta.conditions_count', {
        count: list.length,
        defaultValue: `${list.length} condition(s)`,
      })}`;
    }
    return t('mobile.mta.conditions_none', 'No conditions');
  }
  return t('mobile.mta.conditions_none', 'No conditions');
}

export function MobileMtaRuleManagerPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const [rules, setRules] = useState<MobileRuleRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [eventFacet, setEventFacet] = useState<EventFacet>('all');
  const [selected, setSelected] = useState<MobileRuleRow | null>(null);
  const [editing, setEditing] = useState<MobileRuleRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data: rawRules, error: rErr } = await apiDb
        .from('mta_automation_rule')
        .select('mta_automation_rule_id, internal_code, entity_name, on_event, conditions, is_active, description')
        .order('entity_name')
        .order('internal_code');
      if (rErr) throw rErr;

      // Roll up the rule's actions — a scannable summary of the desktop
      // rule/action editor (count on the card, full list in the detail sheet).
      const { data: rawActions, error: aErr } = await apiDb
        .from('mta_action')
        .select('mta_action_id, mta_automation_rule_id, action_type, channel, is_active, sort_order')
        .order('sort_order');
      if (aErr) throw aErr;

      type RawAction = MobileActionRow & { mta_automation_rule_id: number };
      const byRule: Record<number, MobileActionRow[]> = {};
      for (const a of (rawActions ?? []) as RawAction[]) {
        const list = byRule[a.mta_automation_rule_id] ?? (byRule[a.mta_automation_rule_id] = []);
        list.push({
          mta_action_id: a.mta_action_id,
          action_type: a.action_type,
          channel: a.channel,
          is_active: a.is_active,
          sort_order: a.sort_order,
        });
      }

      type RawRule = {
        mta_automation_rule_id: number;
        internal_code: string;
        entity_name: string;
        on_event: OnEvent;
        conditions: unknown;
        is_active: 'Y' | 'N';
        description: string | null;
      };
      const list: MobileRuleRow[] = ((rawRules ?? []) as RawRule[]).map((r) => {
        const actions = byRule[r.mta_automation_rule_id] ?? [];
        return {
          pk: r.mta_automation_rule_id,
          internal_code: r.internal_code,
          entity_name: r.entity_name,
          on_event: r.on_event,
          conditions: r.conditions,
          is_active: r.is_active,
          description: r.description,
          actions,
          active_action_count: actions.filter((a) => a.is_active === 'Y').length,
        };
      });
      setRules(list);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rules.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (eventFacet !== 'all' && r.on_event !== eventFacet) return false;
      if (!q) return true;
      return (
        r.internal_code.toLowerCase().includes(q) ||
        r.entity_name.toLowerCase().includes(q) ||
        r.on_event.toLowerCase().includes(q) ||
        (r.description ?? '').toLowerCase().includes(q)
      );
    });
  }, [rules, filter, statusFacet, eventFacet]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.mta.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const eventFacets: { key: EventFacet; label: string }[] = [
    { key: 'all', label: t('mobile.mta.event_all', 'All events') },
    ...ON_EVENT_OPTIONS.map((o) => ({ key: o.value as EventFacet, label: t(o.key, o.fallback) })),
  ];

  const actionsLabel = (n: number) =>
    t('mobile.mta.actions_count', { count: n, defaultValue: `${n} actions` });

  const toggleActive = async (r: MobileRuleRow) => {
    setActionBusy(true);
    setError(null);
    const next = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'mta_automation_rule', 'mta_automation_rule_id', r.pk, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.mta.title', 'Automation Rules')}
        description={t('mobile.mta.description', 'Rules that fire tasks and messages on record changes.')}
      />

      <MobileStatStrip
        stats={[
          { value: rules.length, label: t('mobile.mta.stat.total', 'Total') },
          { value: rules.filter((r) => r.is_active === 'Y').length, label: t('mobile.mta.stat.active', 'Active') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.mta.search', 'Search automation rules')}
          aria-label={t('mobile.mta.search', 'Search automation rules')}
        />
      </div>

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      <MobileFilterChips
        chips={eventFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => eventFacet === v}
        onSelect={(v) => setEventFacet(v as EventFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.mta.none', 'No automation rules match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.pk}
              lead={<Zap />}
              leadTone={r.is_active === 'Y' ? 'blue' : 'slate'}
              title={r.internal_code}
              subtitle={`${r.entity_name} · ${eventLabel(t, r.on_event)} · ${actionsLabel(r.actions.length)}`}
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.mta.detail', 'Automation rule detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Zap size={20} /></span>
              <span>
                <div className="m-nm">{selected.internal_code}</div>
                <div className="m-em">{selected.entity_name}</div>
              </span>
            </div>

            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.mta.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.mta.event_lbl', 'On event')} value={eventLabel(t, selected.on_event)} />
              <MobileDetailRow label={t('mobile.mta.conditions', 'Conditions')} value={conditionsSummary(t, selected.conditions)} />
              <MobileDetailRow label={t('mobile.mta.description', 'Description')} value={selected.description || t('common.none', 'None')} />
            </MobileContentCard>

            <MobileContentCard>
              {selected.actions.length === 0 ? (
                <div className="m-empty">{t('mobile.mta.no_actions', 'No actions — add on desktop')}</div>
              ) : (
                selected.actions.map((a, idx) => (
                  <MobileDetailRow
                    key={a.mta_action_id}
                    label={`${idx + 1}. ${actionTypeLabel(t, a.action_type)}`}
                    value={
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {a.action_type === 'MESSAGE' && a.channel ? channelLabel(t, a.channel) : ''}
                        <MobileStatusPill tone={a.is_active === 'Y' ? 'ok' : 'no'}>
                          {a.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                        </MobileStatusPill>
                      </span>
                    }
                  />
                ))
              )}
            </MobileContentCard>

            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.mta.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobileMtaRuleEditSheet
        session={session}
        rule={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit sheet — the scalar rule fields a phone can safely edit (On event,
// Description, Active), saved through the Common Routine (routedUpdate) — §2.
// Entity, conditions JSON and the mta_action rows stay on the desktop and are
// shown read-only here (mirrors MobileUserEditSheet's read-only roles).
// ---------------------------------------------------------------------------

interface MobileMtaRuleEditSheetProps {
  session: AuthSession;
  rule: MobileRuleRow | null;
  onClose: () => void;
  onSaved: () => void;
}

function MobileMtaRuleEditSheet({ session, rule, onClose, onSaved }: MobileMtaRuleEditSheetProps) {
  const { t } = useTranslation();
  const [onEvent, setOnEvent] = useState<OnEvent>('CHANGE');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different rule is opened.
  useEffect(() => {
    if (!rule) return;
    setOnEvent(rule.on_event);
    setDescription(rule.description ?? '');
    setActive(rule.is_active === 'Y');
    setError(null);
  }, [rule]);

  const save = async () => {
    if (!rule) return;
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'mta_automation_rule', 'mta_automation_rule_id', rule.pk, {
      on_event: onEvent,
      description: description.trim() ? description.trim() : null,
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = rule !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.mta.edit_sheet', 'Edit automation rule')}>
        <div className="m-grabber" />
        {rule && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Zap size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.mta.edit_title', 'Edit rule')}</div>
                <div className="m-em">{rule.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label>{t('mobile.mta.entity', 'Entity')}</label>
                <div className="m-formrow-ro">
                  {rule.entity_name}
                  <span className="m-formrow-hint"> · {t('mobile.mta.desktop_only', 'edit on desktop')}</span>
                </div>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-mta-event">{t('mobile.mta.event_lbl', 'On event')}</label>
                <select
                  id="m-mta-event"
                  className="m-sel"
                  value={onEvent}
                  onChange={(e) => setOnEvent(e.target.value as OnEvent)}
                >
                  {ON_EVENT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{t(o.key, o.fallback)}</option>
                  ))}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-mta-desc">{t('mobile.mta.description', 'Description')}</label>
                <textarea
                  id="m-mta-desc"
                  className="m-inp"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.mta.active_hint', 'Rule fires on matching writes')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow">
                <label>{t('mobile.mta.conditions', 'Conditions')}</label>
                <div className="m-formrow-ro">
                  {conditionsSummary(t, rule.conditions)}
                  <span className="m-formrow-hint"> · {t('mobile.mta.desktop_only', 'edit on desktop')}</span>
                </div>
              </div>
              <div className="m-formrow">
                <label>{t('mobile.mta.actions', 'Actions')}</label>
                <div className="m-formrow-ro">
                  {t('mobile.mta.actions_count', { count: rule.actions.length, defaultValue: `${rule.actions.length} actions` })}
                  <span className="m-formrow-hint"> · {t('mobile.mta.desktop_only', 'edit on desktop')}</span>
                </div>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
